import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import {
  spot as spotCore,
  type AutopilotPauseRequest,
  type AutopilotView,
  type SpotStopMode,
} from "@auto-pools/core";
import { api, ApiError } from "../api/client";

/**
 * Pause is a two-option dialog (design 47, section 2); neither option ever disarms a stop. When
 * an `AutopilotView` with an actively running spot leg is passed (design 54), a second question
 * decides what that leg's coins do, Keep preselected (the operator's 01.11 choice). Absent
 * `view`/an inactive spot leg sends no `spotMode` at all, which the server treats as `sell`.
 */
export function AutopilotPauseDialog({
  view,
  onClose,
  onPaused,
}: {
  view?: AutopilotView | null;
  onClose: () => void;
  onPaused: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const keep = useRef<HTMLButtonElement>(null);
  const idempotencyKey = useMemo(() => crypto.randomUUID(), []);
  const [mode, setMode] = useState<AutopilotPauseRequest["mode"]>("keep");
  const spotActive = spotCore.spotPausable(view?.spot ?? null);
  const [spotMode, setSpotMode] = useState<SpotStopMode>("keep");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const previous = document.activeElement,
      element = dialog.current;
    element?.showModal();
    keep.current?.focus();
    return () => {
      element?.close();
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    };
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      await api("/autopilot/pause", {
        method: "POST",
        body: {
          mode,
          idempotencyKey,
          ...(spotActive ? { spotMode } : {}),
        } satisfies AutopilotPauseRequest,
        sensitive: true,
      });
      onPaused();
    } catch (reason) {
      setError(
        reason instanceof ApiError
          ? reason.message
          : reason instanceof Error
            ? reason.message
            : "Pause was not accepted",
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <dialog
      ref={dialog}
      className="position-action-modal autopilot-dialog autopilot-pause-dialog"
      aria-labelledby="autopilot-pause-title"
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
    >
      <header className="modal-header">
        <div>
          <h2 id="autopilot-pause-title">Pause the autopilot</h2>
          <small>While an operation is in flight the run shows Stopping…</small>
        </div>
        <button type="button" className="icon-button" aria-label="Close" onClick={onClose}>
          ×
        </button>
      </header>
      <form onSubmit={(e) => void submit(e)}>
        <div className="position-action-body autopilot-form">
          <fieldset className="autopilot-pause-options">
            <legend className="sr-only">Pause mode</legend>
            <label className={mode === "keep" ? "selected" : ""}>
              <input
                type="radio"
                name="autopilot-pause-mode"
                checked={mode === "keep"}
                onChange={() => {
                  setMode("keep");
                }}
              />
              <span>
                <b>Pause, keep the position</b>
                <small>Nothing new is opened; the open position keeps its stops.</small>
              </span>
            </label>
            <label className={mode === "close" ? "selected" : ""}>
              <input
                type="radio"
                name="autopilot-pause-mode"
                checked={mode === "close"}
                onChange={() => {
                  setMode("close");
                }}
              />
              <span>
                <b>Pause and close the position</b>
                <small>Closes to USDC through the existing close, then pauses.</small>
              </span>
            </label>
          </fieldset>
          {spotActive && (
            <fieldset className="autopilot-pause-options spot-pause-options">
              <legend>Spot · Bybit</legend>
              <label className={spotMode === "sell" ? "selected" : ""}>
                <input
                  type="radio"
                  name="autopilot-pause-spot-mode"
                  checked={spotMode === "sell"}
                  onChange={() => {
                    setSpotMode("sell");
                  }}
                />
                <span>
                  <b>Sell the coins back to USDT</b>
                  <small>Nothing is left exposed to the market while paused.</small>
                </span>
              </label>
              <label className={spotMode === "keep" ? "selected" : ""}>
                <input
                  type="radio"
                  name="autopilot-pause-spot-mode"
                  checked={spotMode === "keep"}
                  onChange={() => {
                    setSpotMode("keep");
                  }}
                />
                <span>
                  <b>Keep the coins</b>
                  <small>
                    Default. The daily review stops; whatever is held stays held until resumed.
                  </small>
                </span>
              </label>
            </fieldset>
          )}
          {error && (
            <p className="inline-alert warning" role="alert">
              {error}
            </p>
          )}
        </div>
        <footer>
          <button ref={keep} type="button" className="button" onClick={onClose}>
            Back
          </button>
          <button className="button primary" disabled={pending}>
            {pending ? "Pausing…" : "Pause autopilot"}
          </button>
        </footer>
      </form>
    </dialog>
  );
}
